const normalizeText = (value) => String(value ?? "").trim();
const resolveAdvanceDisplay = (value) =>
  normalizeText(value).toLowerCase() === "activo" ? "Inactivo" : "Activo";

const parseDateTimeValue = (value) => {
  const raw = normalizeText(value);
  if (!raw || raw === "-") return null;

  const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
  if (match) {
    const [, day, month, year, hours = "00", minutes = "00"] = match;
    const parsed = new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const parsed = parseDateTimeValue(value);
  return parsed
    ? parsed.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "";
};

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }

  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(numeric);
};

const formatPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "";
  }

  return `${numeric > 0 ? "+" : ""}${numeric.toFixed(Math.abs(numeric) >= 10 ? 0 : 1)}%`;
};

const safeJoin = (values, separator = " · ") =>
  values.map(normalizeText).filter(Boolean).join(separator);

const parseJsonContext = (value) => {
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");

const normalizeNumberOrNull = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }

  return null;
};

const buildMetricBadges = (detailData) => {
  const metrics = [
    { key: "today", label: "Hoy", value: detailData.billingToday },
    { key: "seven-day-average", label: "Prom. 7 días", value: detailData.sevenDayAverage },
    { key: "variation", label: "Variación", value: detailData.variationPercent },
    { key: "days-without-sales", label: "Días sin operar", value: detailData.daysWithoutSales },
    { key: "billing-30-days", label: "Facturación 30 días", value: detailData.billing30Days },
    { key: "historical-average", label: "Prom. histórico", value: detailData.historicalAverage },
  ];

  return metrics.filter((metric) => metric.value !== "" && metric.value !== null && metric.value !== undefined);
};

const resolvePriorityTone = (priority) => {
  const normalized = normalizeText(priority).toUpperCase();
  if (normalized === "CRITICA") return "critical";
  if (normalized === "ALTA") return "high";
  if (normalized === "MEDIA") return "medium";
  if (normalized === "BAJA") return "low";
  return "neutral";
};

const normalizeBillingItem = (item = {}) => {
  const scoreRaw = pickFirst(item?.scorePrioridad, item?.ScorePrioridad, item?.score, item?.Score);
  const score = normalizeNumberOrNull(scoreRaw);
  const rawContext = parseJsonContext(item?.datosContexto || item?.DatosContexto);
  const segment = normalizeText(pickFirst(rawContext?.segmento, rawContext?.segment));
  const classification = normalizeText(pickFirst(rawContext?.etiqueta, rawContext?.clasificacion));

  return {
    id: Number(item?.id || item?.Id || 0),
    processId: Number(item?.id || item?.Id || 0),
    processCode: normalizeText(item?.procesoCodigo || item?.ProcesoCodigo || "TERMINAL_SIN_FACTURACION_V2"),
    processSlug: normalizeText(item?.procesoSlug || item?.ProcesoSlug || "terminal-sin-facturacion"),
    commerceName: normalizeText(item?.comercio || item?.Comercio || item?.razonSocial || item?.RazonSocial) || "Sin comercio",
    cuit: normalizeText(item?.cuit || item?.Cuit),
    terminal: normalizeText(item?.numeroTerminal || item?.NumeroTerminal),
    advanceActive: resolveAdvanceDisplay(item?.anticipoActivo || item?.AnticipoActivo),
    province: normalizeText(pickFirst(item?.provincia, item?.Provincia, rawContext?.provincia, rawContext?.Provincia)),
    phone: normalizeText(pickFirst(item?.telefono, item?.Telefono, rawContext?.telefono, rawContext?.Telefono)),
    status: normalizeText(item?.estadoLabel || item?.EstadoLabel || item?.estadoActual || item?.EstadoActual) || "Sin estado",
    score,
    priority: normalizeText(item?.prioridad || item?.Prioridad),
    mainReason: normalizeText(pickFirst(item?.motivoCreacion, item?.MotivoCreacion, rawContext?.motivoPrincipal, rawContext?.motivo)),
    pattern: normalizeText(pickFirst(item?.patronFacturacion, item?.PatronFacturacion, rawContext?.patronFacturacion, rawContext?.patron)),
    suggestedAction: normalizeText(pickFirst(item?.accionPermitida, item?.AccionPermitida, rawContext?.accionSugerida)),
    lastContactAt: normalizeText(pickFirst(item?.ultimaGestion, item?.UltimaGestion, rawContext?.ultimoContacto)),
    nextContactAt: normalizeText(item?.fechaProximaAccion || item?.FechaProximaAccion),
    responsibleName: normalizeText(item?.responsable || item?.Responsable),
    billingToday: formatCurrency(pickFirst(item?.montoHoy, item?.MontoHoy, rawContext?.montoHoy, rawContext?.totalHoy)),
    sevenDayAverage: formatCurrency(pickFirst(item?.promedio7Dias, item?.Promedio7Dias, rawContext?.promedio7Dias)),
    variationPercent: formatPercent(pickFirst(item?.variacion, item?.Variacion, rawContext?.variacion, rawContext?.variationPercent)),
    daysWithoutSales: normalizeNumberOrNull(item?.diasSinVentas, item?.DiasSinVentas, rawContext?.diasSinVentas),
    billing30Days: formatCurrency(pickFirst(item?.facturacion30Dias, item?.Facturacion30Dias, rawContext?.facturacion30Dias)),
    historicalAverage: formatCurrency(
      pickFirst(
        item?.facturacionPromedioHistorica,
        item?.FacturacionPromedioHistorica,
        rawContext?.facturacionPromedioHistorica,
        rawContext?.promedioHistorico
      )
    ),
    suggestedMessage: normalizeText(item?.mensajeSugerido || item?.MensajeSugerido),
    segment,
    classification,
    tags: [segment, classification].filter(Boolean),
    canManage: item?.puedeGestionar !== false,
    canTake: Boolean(item?.puedeTomar),
    canAssign: Boolean(item?.puedeAsignar),
    canRelease: Boolean(item?.puedeLiberar),
    readOnly: Boolean(item?.esSoloLectura),
    versionToken: normalizeText(item?.versionToken || item?.VersionToken),
    riskLevel: item?.nivelRiesgo ?? item?.NivelRiesgo ?? null,
    raw: item,
  };
};

const normalizeBillingDetail = (detailResponse) => {
  const instance = detailResponse?.instancia || {};
  const history = Array.isArray(detailResponse?.historial) ? detailResponse.historial : [];
  const context = parseJsonContext(instance?.datosContexto || instance?.DatosContexto);
  const scoreRaw = pickFirst(instance?.scorePrioridad, instance?.ScorePrioridad, context?.scorePrioridad, context?.score);
  const score = normalizeNumberOrNull(scoreRaw);
  const segment = normalizeText(pickFirst(context?.segmento, context?.segment));
  const classification = normalizeText(pickFirst(context?.clasificacion, context?.etiqueta));

  const detail = {
    mainReason: normalizeText(
      pickFirst(instance?.motivoCreacion, instance?.MotivoCreacion, context?.motivoPrincipal, context?.motivo)
    ),
    pattern: normalizeText(
      pickFirst(instance?.patronFacturacion, instance?.PatronFacturacion, context?.patronFacturacion, context?.patron)
    ),
    suggestedAction: normalizeText(
      pickFirst(instance?.accionPermitida, instance?.AccionPermitida, context?.accionSugerida, context?.suggestedAction)
    ),
    lastContactAt: normalizeText(pickFirst(history[0]?.fecha, history[0]?.Fecha, context?.ultimoContacto)),
    nextContactAt: normalizeText(instance?.fechaProximaAccion || instance?.FechaProximaAccion),
    responsibleName: normalizeText(instance?.responsable || instance?.Responsable),
    billingToday: formatCurrency(pickFirst(context?.montoHoy, context?.totalHoy)),
    sevenDayAverage: formatCurrency(pickFirst(context?.promedio7Dias, context?.promedioSieteDias)),
    variationPercent: formatPercent(pickFirst(context?.variacion, context?.variationPercent)),
    daysWithoutSales: normalizeNumberOrNull(context?.diasSinVentas),
    billing30Days: formatCurrency(pickFirst(context?.facturacion30Dias)),
    historicalAverage: formatCurrency(pickFirst(context?.facturacionPromedioHistorica, context?.promedioHistorico)),
    suggestedMessage: normalizeText(instance?.mensajeSugerido || instance?.MensajeSugerido),
    segment,
    classification,
    tags: [segment, classification].filter(Boolean),
    score,
  };

  detail.metricBadges = buildMetricBadges(detail);
  return detail;
};

const filterBillingRows = (rows, search) => {
  const needle = normalizeText(search).toLowerCase();
  if (!needle) return rows;

  return rows.filter((item) =>
    [
      item.commerceName,
      item.cuit,
      item.terminal,
      item.province,
      item.phone,
      item.responsibleName,
    ]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(needle))
  );
};

const resolveActionLabel = (item) =>
  item.readOnly || item.canManage === false ? "Ver detalle" : "Gestionar";

export {
  filterBillingRows,
  formatDate,
  normalizeBillingDetail,
  normalizeBillingItem,
  normalizeText,
  parseDateTimeValue,
  resolveActionLabel,
  resolvePriorityTone,
  safeJoin,
};
